import { useCallback, useEffect, useRef, useState } from 'react';

interface StickyTabsProps {
  hasListings: boolean;
  hasSimilar: boolean;
  hasSpecifications: boolean;
  hasReviews: boolean;
  hasFaq: boolean;
  hasVideos: boolean;
  hasPricingGuide?: boolean;
  hasResources?: boolean;
}

interface Tab {
  id: string;
  label: string;
  sectionId: string;
}

const SCROLL_THRESHOLD = 200;

const ALL_TABS: Tab[] = [
  { id: 'buy', label: 'Buy', sectionId: 'buy' },
  { id: 'listings', label: 'Listings', sectionId: 'listings' },
  { id: 'overview', label: 'Overview', sectionId: 'overview' },
  { id: 'specifications', label: 'Specifications', sectionId: 'specifications' },
  { id: 'pricing-guide', label: 'Pricing Guide', sectionId: 'pricing-guide' },
  { id: 'reviews', label: 'Reviews', sectionId: 'reviews' },
  { id: 'videos', label: 'Videos', sectionId: 'videos' },
  { id: 'resources', label: 'Resources', sectionId: 'resources' },
  { id: 'faq', label: 'FAQs', sectionId: 'faq' },
  { id: 'similar', label: 'Similar', sectionId: 'similar' },
];

export default function StickyTabs({
  hasListings,
  hasSimilar,
  hasSpecifications,
  hasReviews,
  hasFaq,
  hasVideos,
  hasPricingGuide,
  hasResources,
}: StickyTabsProps) {
  const visibleTabs = ALL_TABS.filter((tab) => {
    if (tab.id === 'buy') return hasListings;
    if (tab.id === 'listings') return hasListings;
    if (tab.id === 'pricing-guide') return !!hasPricingGuide;
    if (tab.id === 'overview') return true;
    if (tab.id === 'similar') return hasSimilar;
    if (tab.id === 'specifications') return hasSpecifications;
    if (tab.id === 'reviews') return hasReviews;
    if (tab.id === 'videos') return hasVideos;
    if (tab.id === 'resources') return !!hasResources;
    if (tab.id === 'faq') return hasFaq;
    return false;
  });

  const [activeTab, setActiveTab] = useState<string>(hasListings ? 'buy' : 'overview');
  const isManualScrollRef = useRef(false);
  const manualScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = useCallback(() => {
    if (isManualScrollRef.current) return;

    let current = '';
    for (const tab of visibleTabs) {
      const el = document.getElementById(tab.sectionId);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= SCROLL_THRESHOLD && rect.bottom >= SCROLL_THRESHOLD) {
          current = tab.id;
          break;
        }
      }
    }

    if (current && current !== activeTab) {
      setActiveTab(current);
    }
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      if (manualScrollTimerRef.current) clearTimeout(manualScrollTimerRef.current);
    };
  }, []);

  const handleTabClick = useCallback((tab: Tab) => {
    setActiveTab(tab.id);

    const el = document.getElementById(tab.sectionId);
    if (!el) return;

    isManualScrollRef.current = true;
    if (manualScrollTimerRef.current) clearTimeout(manualScrollTimerRef.current);

    const elementTop = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: elementTop - 180, behavior: 'smooth' });

    manualScrollTimerRef.current = setTimeout(() => {
      isManualScrollRef.current = false;
    }, 800);
  }, []);

  if (visibleTabs.length === 0) return null;

  return (
    <>
      <style>{`
        .sticky-tabs-scroll::-webkit-scrollbar { display: none; }
        .sticky-tabs-scroll { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>

      <nav
        aria-label="Page sections"
        // Stick just below the sticky header. --gf-header-h is published by
        // Navbar and tracks its scroll collapse + the xl-only CategoryNav, so
        // this stays flush instead of using a hardcoded old-header height.
        style={{ top: 'var(--gf-header-h)' }}
        className="sticky z-40 bg-white border-b border-[#CDCDCD] mb-[3rem]"
      >
        <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x">
          <div
            role="tablist"
            aria-label="Section navigation"
            className="sticky-tabs-scroll flex overflow-x-auto gap-[2rem] whitespace-nowrap"
          >
            {visibleTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={tab.sectionId}
                  onClick={() => handleTabClick(tab)}
                  className={[
                    'flex-shrink-0 py-[1.2rem] text-[1.4rem] sm:text-[1.6rem] transition-colors duration-150',
                    'border-b-[3px] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[#C75008] focus-visible:ring-offset-1',
                    isActive
                      ? 'border-[#C75008] font-bold text-[#C75008]'
                      : 'border-transparent font-normal text-brand-gray hover:text-brand-dark-gray',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}
